// Shared AG-Grid export helpers for the common-data grids
// (Instrument Details, Tickers Master, MongoDB Explorer). Kept
// framework-agnostic so any component with an ag-grid api can lift
// its own Copy menu on top.

/**
 * Rows the operator most likely wants to act on:
 *   1. If any rows are checkbox-selected, use those.
 *   2. Otherwise, use every row currently visible after filter+sort.
 * Matches the convention from commerce-events.component.
 */
export function getEffectiveRows<T = any>(gridApi: any): T[] {
  if (!gridApi) return [];
  const selected = gridApi.getSelectedRows?.() as T[] | undefined;
  if (selected && selected.length > 0) return selected;
  const rows: T[] = [];
  gridApi.forEachNodeAfterFilterAndSort?.((n: any) => { if (n?.data) rows.push(n.data as T); });
  return rows;
}

/**
 * Ordered field IDs of every currently-visible column, respecting
 * the operator's drag-reorder + hide from the column tool panel.
 * Skips columns that have no `field` (checkbox / renderer-only cols).
 */
export function visibleFieldsFor(gridApi: any): string[] {
  if (!gridApi) return [];
  const cols = gridApi.getAllDisplayedColumns?.() || [];
  const out: string[] = [];
  for (const c of cols) {
    const field = c?.getColDef?.()?.field;
    if (typeof field === 'string' && field) out.push(field);
  }
  return out;
}

/**
 * Ordered column header names matching visibleFieldsFor(). Uses the
 * column's user-visible headerName (or field as fallback) so exported
 * CSV/TSV headers read the same as the on-screen grid.
 */
export function visibleHeadersFor(gridApi: any): string[] {
  if (!gridApi) return [];
  const cols = gridApi.getAllDisplayedColumns?.() || [];
  const out: string[] = [];
  for (const c of cols) {
    const cd = c?.getColDef?.();
    const field = cd?.field;
    if (typeof field !== 'string' || !field) continue;
    out.push(String(cd?.headerName || cd?.field));
  }
  return out;
}

/** JSON dump — pretty-printed for readable clipboard paste. */
export function rowsToJson(rows: any[]): string {
  try { return JSON.stringify(rows, null, 2); }
  catch { return '[]'; }
}

/**
 * Emit rows as delimited text (CSV or TSV). Always includes a header
 * row when there are 2+ rows OR when explicitly requested — Mike's
 * spec: "multi select gets a heading row".
 */
export function rowsToDelimited(rows: any[], fields: string[], headers: string[], delimiter: ',' | '\t' = ','): string {
  const needsHeader = rows.length >= 2;
  const esc = (v: unknown): string => {
    const s = v === null || v === undefined ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
    if (delimiter === '\t') {
      // TSV: escape tabs + newlines within cells; wrap in quotes if
      // the cell contains a double-quote so paste-into-sheets round-
      // trips cleanly.
      const clean = s.replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
      return /["]/.test(clean) ? `"${clean.replace(/"/g, '""')}"` : clean;
    }
    // CSV: standard escaping — quote when needed, double-quote inside quotes.
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines: string[] = [];
  if (needsHeader) lines.push(headers.map(esc).join(delimiter));
  for (const r of rows) lines.push(fields.map((f) => esc(r?.[f])).join(delimiter));
  return lines.join('\n');
}

/** Fire-and-forget clipboard write. Returns success. */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
